import apiClient, { safeJson } from '@/api/client';
import {
    ICenterMember,
    CreateCenterMemberBody,
    UpdateCenterMemberBody,
    ConvertMemberTypeBody,
    BulkUploadResult,
    PublicMemberListParams,
    PublicMemberListResponse,
    PublicMemberDetailResponse,
    MemberArchiveListResponse,
} from './types';

/**
 * @method get
 * @brief 센터 회원 목록 조회
 */
export const getCenterMembers = async ({
    centerId,
    memberType,
    status,
    search,
    hasPT,
    memberScope,
    coach,
    remainingPeriod,
    remainingSessions,
    displayStatus,
    facilityStatus,
    appAccountStatus,
    appLinked,
    page,
    limit,
    timeout,
}: PublicMemberListParams & { timeout?: number }) => {
    const searchParams: Record<string, string> = {};
    if (memberType && memberType !== 'all') searchParams.memberType = memberType;
    if (status && status !== 'all') searchParams.status = status;
    if (search) searchParams.search = search;
    if (hasPT) searchParams.hasPT = hasPT;
    if (memberScope && memberScope !== 'all') searchParams.memberScope = memberScope;
    if (coach && coach !== 'all') searchParams.coach = coach;
    if (remainingPeriod && remainingPeriod !== 'all') searchParams.remainingPeriod = remainingPeriod;
    if (remainingSessions && remainingSessions !== 'all') searchParams.remainingSessions = remainingSessions;
    if (displayStatus && displayStatus !== 'all') searchParams.displayStatus = displayStatus;
    if (facilityStatus && facilityStatus !== 'all') searchParams.facilityStatus = facilityStatus;
    if (appAccountStatus && appAccountStatus !== 'all') {
        searchParams.appAccountStatus = appAccountStatus;
    } else if (appLinked && appLinked !== 'all') {
        // 구형 호출부 호환. 신규 앱 계정 상태 필터가 있으면 전송하지 않는다.
        searchParams.appLinked = appLinked;
    }
    if (page !== undefined) searchParams.page = String(page);
    if (limit !== undefined) searchParams.limit = String(limit);

    const res = await apiClient.get(`v1/centers/${centerId}/members`, {
        searchParams,
        ...(timeout !== undefined ? { timeout } : {}),
    });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as PublicMemberListResponse | null,
    };
};

/**
 * @method get
 * @brief 센터 회원 상세 조회
 */
export const getCenterMember = async ({
    centerId,
    memberId,
}: {
    centerId: string;
    memberId: string;
}) => {
    const res = await apiClient.get(`v1/centers/${centerId}/members/${memberId}`);
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as PublicMemberDetailResponse | null,
    };
};

/**
 * @method post
 * @brief 센터 회원 단건 등록
 */
export const createCenterMember = async ({
    centerId,
    body,
}: {
    centerId: string;
    body: CreateCenterMemberBody;
}) => {
    const res = await apiClient.post(`v1/centers/${centerId}/members`, {
        json: body,
    });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as ICenterMember | null,
    };
};

/**
 * @method post
 * @brief 센터 회원 엑셀 일괄 등록
 */
export const bulkCreateCenterMembers = async ({
    centerId,
    file,
}: {
    centerId: string;
    file: File;
}) => {
    const formData = new FormData();
    formData.append('file', file);

    const res = await apiClient.post(`v1/centers/${centerId}/members/bulk`, {
        body: formData,
    });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as BulkUploadResult | null,
    };
};

/**
 * @method put
 * @brief 센터 회원 정보 수정
 */
export const updateCenterMember = async ({
    centerId,
    memberId,
    body,
}: {
    centerId: string;
    memberId: string;
    body: UpdateCenterMemberBody;
}) => {
    const res = await apiClient.put(`v1/centers/${centerId}/members/${memberId}`, {
        json: body,
    });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as ICenterMember | null,
    };
};

/**
 * @method delete
 * @brief 센터 회원 연결 해제 (soft delete)
 */
export const deleteCenterMember = async ({
    centerId,
    memberId,
    confirmShareCleanup,
    expectedItemIds,
}: {
    centerId: string;
    memberId: string;
    /**
     * 이 회원이 소유한 공유 풀을 정리하고 종료할지. **프리뷰를 확인했을 때만 true.**
     * 보내지 않으면 서버가 오늘과 똑같이 SHARE_OWNER_DISCONNECT_BLOCKED 로 막는다.
     *
     * ⚠️ **지금 이 값을 보내는 화면은 없다**(회원 삭제 자동 정리는 별건으로 분리).
     * optional 이라 안 보내면 기존 동작 그대로다. 별건 작업이 그대로 다시 쓴다 — 지우지 마라.
     */
    confirmShareCleanup?: boolean;
    /** 확인한 취소 대상 id 전체(풀 구분 없는 합집합). 빠지면 정리가 거절된다. */
    expectedItemIds?: string[];
}) => {
    // 서버는 `?expectedItemIds=a,b,c` 와 반복 파라미터를 모두 받는다. 쉼표 형태가 짧다.
    /**
     * 0건이면 `expectedItemIds` 를 아예 싣지 않는다.
     *
     * 서버는 값 없음을 "확인 안 됨"으로 다루지만, **쿼리스트링으로는 빈 배열을 표현할
     * 방법이 없다** — `?expectedItemIds=` 도 서버 `parseShareCleanupItemIds` 가
     * `undefined` 로 읽는다(빈 문자열 분기). 그래서 빈 값을 실어도 동작이 같다.
     *
     * 0건 프리뷰 직후 새 예약이 들어오면 정리가 돌지 않아 삭제가 다시 막히는데,
     * 그때 서버가 SHARE_OWNER_DISCONNECT_BLOCKED 를 다시 주므로 화면이 프리뷰를 새로
     * 받아 **새 계획을 연다.** 무반응이 아니라 새 목록으로 다시 확인받는 흐름이다.
     */
    const query = confirmShareCleanup
        ? `?confirmShareCleanup=true${
              expectedItemIds && expectedItemIds.length > 0
                  ? `&expectedItemIds=${encodeURIComponent(expectedItemIds.join(','))}`
                  : ''
          }`
        : '';
    const res = await apiClient.delete(`v1/centers/${centerId}/members/${memberId}${query}`);
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as { success: boolean } | null,
    };
};

/**
 * @method post
 * @brief 회원 타입 전환
 */
export const convertMemberType = async ({
    centerId,
    memberId,
    body,
}: {
    centerId: string;
    memberId: string;
    body: ConvertMemberTypeBody;
}) => {
    const res = await apiClient.post(`v1/centers/${centerId}/members/${memberId}/convert`, {
        json: body,
    });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as ICenterMember | null,
    };
};

/**
 * @method get
 * @brief 센터 과거 회원 (아카이브) 목록 조회
 */
export const getCenterMemberArchives = async ({
    centerId,
    search,
    page,
    limit,
}: {
    centerId: string;
    search?: string;
    page?: number;
    limit?: number;
}) => {
    const searchParams: Record<string, string> = {};
    if (search) searchParams.search = search;
    if (page !== undefined) searchParams.page = String(page);
    if (limit !== undefined) searchParams.limit = String(limit);

    const res = await apiClient.get(`v1/centers/member-archives/${centerId}`, { searchParams });
    const data = await safeJson(res);
    return {
        status: res.status,
        data: data as MemberArchiveListResponse | null,
    };
};
